import React, { useState, useEffect } from 'react';
import AppSidebar from '@/components/AppSidebar';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, Activity, RefreshCw, TrendingDown, Shield, Menu, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCAlertsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [scaAlerts, setScaAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sortBy, setSortBy] = useState('timestamp'); // New state for sorting
  const [showAssetList, setShowAssetList] = useState(false); // Show affected assets list
  const alertsRef = React.useRef(null); // Reference for scrolling to alerts

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/login');
      return;
    }

    setUser(JSON.parse(userData));
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [alertsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/sca-alerts`, config),
        axios.get(`${API}/sca-alerts/summary`, config)
      ]);

      setScaAlerts(alertsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to load SCA alerts');
    } finally {
      setLoading(false);
    }
  };

  const generateAlerts = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/sca-alerts/generate`, {}, config);
      toast.success('SCA alerts generated');
      await loadData();
    } catch (error) {
      toast.error('Failed to generate alerts');
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.patch(`${API}/sca-alerts/${alertId}/resolve`, {}, config);
      toast.success('Alert resolved');
      await loadData();
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const filteredAlerts = scaAlerts.filter(alert => {
    if (filterType !== 'all' && alert.alert_type !== filterType) return false;
    if (filterSeverity !== 'all' && alert.severity !== filterSeverity) return false;
    return true;
  });

  // Sort alerts based on sortBy state
  const sortedAlerts = [...filteredAlerts].sort((a, b) => {
    if (sortBy === 'risk') {
      return (b.availability_risk_impact || 0) - (a.availability_risk_impact || 0);
    } else if (sortBy === 'timestamp') {
      return new Date(b.timestamp) - new Date(a.timestamp);
    }
    return 0;
  });

  // Get list of affected assets with their alert details
  const getAffectedAssets = () => {
    const assetMap = {};
    
    scaAlerts.filter(alert => alert.status === 'Open').forEach(alert => {
      if (!assetMap[alert.asset_id]) {
        assetMap[alert.asset_id] = {
          id: alert.asset_id,
          name: alert.asset_name,
          alerts: [],
          criticalCount: 0,
          warningCount: 0,
          totalRisk: 0
        };
      }
      
      assetMap[alert.asset_id].alerts.push(alert);
      if (alert.severity === 'Critical') {
        assetMap[alert.asset_id].criticalCount++;
      } else if (alert.severity === 'Warning') {
        assetMap[alert.asset_id].warningCount++;
      }
      assetMap[alert.asset_id].totalRisk += alert.availability_risk_impact || 0;
    });
    
    // Convert to array and sort by total risk (descending)
    return Object.values(assetMap).sort((a, b) => b.totalRisk - a.totalRisk);
  };

  const getSeverityColor = (severity) => {
    return severity === 'Critical' ? 'text-red-600 bg-red-50' : 'text-yellow-600 bg-yellow-50';
  };

  const getRiskColor = (risk) => {
    if (risk < 30) return 'text-green-600';
    if (risk < 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="sca-alerts-page">
      <AppSidebar />

      {/* Main Content */}
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center space-x-4">
                <Button variant="ghost" onClick={() => setSidebarOpen(!sidebarOpen)}>
                  <Menu className="w-5 h-5" />
                </Button>
                <h1 className="text-4xl font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  SCA Monitoring Alerts
                </h1>
              </div>
              <p className="text-gray-600 mt-2">System Control and Automation predictive alerts</p>
            </div>
            {user?.role === 'admin' && (
              <Button
                onClick={generateAlerts}
                className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Generate Test Alerts
              </Button>
            )}
          </div>

          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card 
                className="bg-white border-gray-200 hover:shadow-lg transition-all cursor-pointer"
                onClick={() => {
                  setFilterSeverity('all');
                  setFilterType('all');
                  setSortBy('timestamp');
                  alertsRef.current?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Total SCA Alerts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-black">{summary.total_alerts}</div>
                  <p className="text-xs text-gray-600 mt-1">
                    {summary.critical_count} Critical, {summary.warning_count} Warning
                  </p>
                </CardContent>
              </Card>

              <Card 
                className="bg-white border-gray-200 hover:shadow-lg transition-all cursor-pointer"
                onClick={() => {
                  setSortBy('risk');
                  setFilterSeverity('all');
                  setFilterType('all');
                  alertsRef.current?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Availability Risk Index</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-3xl font-bold ${getRiskColor(summary.availability_risk_index)}`}>
                    {summary.availability_risk_index}%
                  </div>
                  <p className="text-xs text-gray-600 mt-1">System downtime risk</p>
                </CardContent>
              </Card>

              <Card 
                className="bg-white border-gray-200 hover:shadow-lg transition-all cursor-pointer"
                onClick={() => {
                  setFilterSeverity('Critical');
                  setSortBy('risk');
                  alertsRef.current?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Predicted Availability</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-3xl font-bold ${getRiskColor(100 - summary.predicted_availability)}`}>
                    {summary.predicted_availability}%
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Expected uptime</p>
                </CardContent>
              </Card>

              <Card 
                className="bg-white border-gray-200 hover:shadow-lg transition-all cursor-pointer"
                onClick={() => {
                  setShowAssetList(true);
                  setFilterSeverity('all');
                  setFilterType('all');
                  setSortBy('timestamp');
                }}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Affected Assets</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-black">{summary.affected_assets_count}</div>
                  <p className="text-xs text-gray-600 mt-1">Sites with alerts</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Affected Assets List View */}
          {showAssetList && (
            <Card className="bg-white border-gray-200 mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-black">Affected Assets - Live Status</CardTitle>
                  <Button
                    variant="outline"
                    onClick={() => setShowAssetList(false)}
                    className="text-gray-700"
                  >
                    Close List
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {getAffectedAssets().length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                      <p className="text-gray-600">No assets currently affected by SCA alerts</p>
                    </div>
                  ) : (
                    <>
                      <div className="bg-green-50 border border-green-200 rounded p-3 mb-4">
                        <p className="text-green-900 text-sm">
                          <strong>Live Data:</strong> This list updates automatically when alerts are resolved. 
                          {getAffectedAssets().length} {getAffectedAssets().length === 1 ? 'asset' : 'assets'} currently affected.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {getAffectedAssets().map((asset, index) => (
                          <Card key={asset.id} className="bg-gray-50 border-gray-300 hover:bg-gray-100 transition-all">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="font-semibold text-black mb-2">{asset.name}</h4>
                                  <div className="flex items-center space-x-3 text-sm">
                                    <Badge className="bg-red-100 text-red-700">
                                      {asset.criticalCount} Critical
                                    </Badge>
                                    <Badge className="bg-yellow-100 text-yellow-700">
                                      {asset.warningCount} Warning
                                    </Badge>
                                  </div>
                                  <div className="mt-2 text-xs text-gray-600">
                                    <div>Total Risk Impact: <span className={`font-semibold ${getRiskColor(asset.totalRisk * 10)}`}>
                                      {asset.totalRisk.toFixed(2)}%
                                    </span></div>
                                    <div>Alert Types:</div>
                                    <ul className="ml-4 mt-1">
                                      {[...new Set(asset.alerts.map(a => a.alert_type))].map((type) => (
                                        <li key={type} className="text-xs">• {type}</li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className={`text-2xl font-bold ${getRiskColor(asset.totalRisk * 10)}`}>
                                    {asset.alerts.length}
                                  </div>
                                  <p className="text-xs text-gray-500">alerts</p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Active Filter/Sort Banner */}
          {(sortBy === 'risk' || filterSeverity !== 'all') && (
            <Card className="bg-blue-50 border-blue-200 mb-6" ref={alertsRef}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <TrendingDown className="w-6 h-6 text-blue-600" />
                    <div>
                      <h3 className="text-blue-900 font-semibold">
                        {sortBy === 'risk' ? 'Sorted by Risk Impact' : 'Filtered View'}
                      </h3>
                      <p className="text-blue-700 text-sm">
                        {sortBy === 'risk' && 'Showing alerts ordered by availability risk impact (highest first)'}
                        {filterSeverity === 'Critical' && 'Showing only Critical severity alerts'}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSortBy('timestamp');
                      setFilterSeverity('all');
                      setFilterType('all');
                    }}
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    Clear
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Filters */}
          <Card className="bg-white border-gray-200 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center space-x-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mr-2">Type:</label>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="all">All Types</option>
                    <option value="Communication & Control Latency">Communication & Control Latency</option>
                    <option value="PCS Dispatch Deviation">PCS Dispatch Deviation</option>
                    <option value="BMS-SCA Data Drift">BMS-SCA Data Drift</option>
                    <option value="Environmental & Auxiliary System Anomalies">Environmental Anomalies</option>
                    <option value="Chemistry Thermal Safety">Chemistry Thermal Safety</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mr-2">Severity:</label>
                  <select
                    value={filterSeverity}
                    onChange={(e) => setFilterSeverity(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="all">All Severities</option>
                    <option value="Critical">Critical</option>
                    <option value="Warning">Warning</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Alerts List */}
          <div className="space-y-4">
            {sortedAlerts.length === 0 ? (
              <Card className="bg-white border-gray-200">
                <CardContent className="p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-600">No SCA alerts found</p>
                </CardContent>
              </Card>
            ) : (
              sortedAlerts.map((alert) => (
                <Card key={alert.id} className="bg-white border-gray-200 hover:shadow-md transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Badge className={getSeverityColor(alert.severity)}>
                            {alert.severity}
                          </Badge>
                          <Badge variant="outline" className="text-gray-600">
                            {alert.alert_type}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {new Date(alert.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-black mb-2">{alert.title}</h3>
                        <p className="text-sm text-gray-700 mb-3">{alert.description}</p>
                        <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
                          <p className="text-sm text-blue-900">
                            <strong>Recommended Action:</strong> {alert.recommended_action}
                          </p>
                        </div>
                        <div className="flex items-center space-x-4 text-xs text-gray-600">
                          <span>Asset: {alert.asset_name}</span>
                          <span>Risk Impact: <span className={getRiskColor(alert.availability_risk_impact * 10)}>
                            {alert.availability_risk_impact.toFixed(2)}%
                          </span></span>
                          <span>Metric: {alert.metric_value?.toFixed(2)} (Threshold: {alert.threshold})</span>
                        </div>
                      </div>
                      {alert.status === 'Open' && user?.role === 'admin' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resolveAlert(alert.id)}
                          className="ml-4"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Resolve
                        </Button>
                      )}
                      {alert.status === 'Resolved' && (
                        <Badge variant="outline" className="text-green-600 ml-4">
                          Resolved
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SCAlertsPage;
